import React, { useState, useEffect, useContext } from 'react';
import { Dialog, DialogTitle, DialogContent, Button, Typography, Tabs, Tab, Box, IconButton, Slide, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DownloadIcon from '@mui/icons-material/Download';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import MonitorIcon from '@mui/icons-material/Monitor';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Store } from '../Store';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PWAInstallPrompt = () => {
  const { state } = useContext(Store);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Retrieve user settings for consistent styling
  let { setting } = state.userSetting || {};
  if (!setting) {
    setting = JSON.parse(localStorage.getItem("userInfo")) || {};
  }
  const themeColor = setting.themeColor || '#543A20';
  const themeTxtColor = setting.themeTxtColor || '#CE9760';

  useEffect(() => {
    // Check if app is already running in standalone mode (installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);

      // Check if user dismissed the prompt recently (within the last 7 days)
      const dismissedTime = localStorage.getItem('pwa-prompt-dismissed-time');
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      const isRecentlyDismissed = dismissedTime && (Date.now() - parseInt(dismissedTime, 10)) < sevenDays;

      if (!isRecentlyDismissed) {
        // Show the banner with a slight delay
        const timer = setTimeout(() => {
          setShowBanner(true);
        }, 3000);
        return () => clearTimeout(timer);
      }
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowBanner(false);
      setShowGuide(false);
      setDeferredPrompt(null);
      console.log('[PWA] MenuLive was successfully installed!');
    };

    // Custom event to trigger guide from anywhere in the app
    const handleOpenPWAGuide = () => {
      setShowGuide(true);
      // Auto-detect platform and pre-select corresponding tab
      const ua = navigator.userAgent.toLowerCase();
      if (/iphone|ipad|ipod/.test(ua)) {
        setActiveTab(0); // iOS Safari
      } else if (/android/.test(ua)) {
        setActiveTab(1); // Android Chrome
      } else {
        setActiveTab(2); // Desktop
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('open-pwa-install', handleOpenPWAGuide);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('open-pwa-install', handleOpenPWAGuide);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`[PWA] User choice outcome: ${outcome}`);
      // Clear the deferred prompt variable
      setDeferredPrompt(null);
      setShowBanner(false);
    } else {
      // Fallback: show the step-by-step guide
      setShowBanner(false);
      setShowGuide(true);
      // Detect OS
      const ua = navigator.userAgent.toLowerCase();
      if (/iphone|ipad|ipod/.test(ua)) {
        setActiveTab(0);
      } else if (/android/.test(ua)) {
        setActiveTab(1);
      } else {
        setActiveTab(2);
      }
    }
  };

  const handleDismissBanner = () => {
    setShowBanner(false);
    // Save current timestamp to ignore prompt for 7 days
    localStorage.setItem('pwa-prompt-dismissed-time', Date.now().toString());
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // If already installed or neither prompt nor custom trigger is relevant, render nothing
  if (isInstalled) return null;

  return (
    <>
      {/* Floating PWA Install Banner */}
      {showBanner && (
        <Paper
          elevation={6}
          style={{
            position: 'fixed',
            bottom: '95px', // Floats beautifully above the standard bottom navigation menu
            left: '50%',
            transform: 'translateX(-50%)',
            width: '90%',
            maxWidth: '450px',
            zIndex: 99999,
            padding: '16px',
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: `1.5px solid ${themeColor}`,
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            animation: 'fadeInUp 0.5s ease-out',
          }}
        >
          {/* Header row */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap="10px">
              <img
                src="/logo192.png"
                alt="MenuLive"
                style={{ width: '40px', height: '40px', borderRadius: '8px', border: `1px solid ${themeColor}` }}
              />
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: themeColor, lineHeight: 1.2 }}>
                  MenuLive App
                </Typography>
                <Typography variant="caption" style={{ color: '#555', display: 'block' }}>
                  Install for a faster, offline ordering experience!
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={handleDismissBanner} style={{ color: themeColor }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Action Row */}
          <Box display="flex" gap="10px" width="100%">
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setShowBanner(false);
                setShowGuide(true);
              }}
              style={{
                flex: 1,
                borderColor: themeColor,
                color: themeColor,
                borderRadius: '24px',
                textTransform: 'none',
                fontWeight: '600',
              }}
            >
              How to Install
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleInstallClick}
              style={{
                flex: 1.2,
                backgroundColor: themeColor,
                color: themeTxtColor,
                borderRadius: '24px',
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              Install Now
            </Button>
          </Box>
        </Paper>
      )}

      {/* Elegant Installation Guide Dialog */}
      <Dialog
        open={showGuide}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setShowGuide(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '20px',
            background: '#ffffff',
            overflow: 'hidden',
          },
        }}
        style={{ zIndex: 999999 }}
      >
        <DialogTitle
          style={{
            backgroundColor: themeColor,
            color: themeTxtColor,
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
          }}
        >
          <Box display="flex" alignItems="center" gap="8px">
            <DownloadIcon />
            <Typography variant="h6" component="span" style={{ fontWeight: 'bold' }}>
              App Installation Guide
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setShowGuide(false)} style={{ color: themeTxtColor }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="inherit"
          style={{
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f8f8f8',
          }}
          TabIndicatorProps={{
            style: {
              backgroundColor: themeColor,
              height: '3px',
            },
          }}
        >
          <Tab
            label="iOS (Safari)"
            icon={<SmartphoneIcon fontSize="small" />}
            iconPosition="start"
            style={{ textTransform: 'none', minHeight: '48px', fontWeight: activeTab === 0 ? 'bold' : 'normal', color: activeTab === 0 ? themeColor : '#777' }}
          />
          <Tab
            label="Android (Chrome)"
            icon={<SmartphoneIcon fontSize="small" />}
            iconPosition="start"
            style={{ textTransform: 'none', minHeight: '48px', fontWeight: activeTab === 1 ? 'bold' : 'normal', color: activeTab === 1 ? themeColor : '#777' }}
          />
          <Tab
            label="Desktop"
            icon={<MonitorIcon fontSize="small" />}
            iconPosition="start"
            style={{ textTransform: 'none', minHeight: '48px', fontWeight: activeTab === 2 ? 'bold' : 'normal', color: activeTab === 2 ? themeColor : '#777' }}
          />
        </Tabs>

        <DialogContent style={{ padding: '24px 20px', backgroundColor: '#fff' }}>
          {/* iOS Instructions */}
          {activeTab === 0 && (
            <Box display="flex" flexDirection="column" gap="16px">
              <Typography variant="body2" color="textSecondary">
                Apple iOS does not allow direct one-click PWA installation. Follow these simple steps using the Safari browser:
              </Typography>
              
              <Box display="flex" gap="12px" alignItems="flex-start">
                <Box
                  style={{
                    backgroundColor: themeColor,
                    color: themeTxtColor,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    flexShrink: 0,
                  }}
                >
                  1
                </Box>
                <Typography variant="body2" style={{ color: '#333' }}>
                  Open this page in <strong>Safari</strong> and tap the <strong>Share</strong> button <ShareIcon fontSize="small" style={{ verticalAlign: 'middle', color: themeColor, margin: '0 2px' }} /> in the browser navigation bar.
                </Typography>
              </Box>

              <Box display="flex" gap="12px" alignItems="flex-start">
                <Box
                  style={{
                    backgroundColor: themeColor,
                    color: themeTxtColor,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    flexShrink: 0,
                  }}
                >
                  2
                </Box>
                <Typography variant="body2" style={{ color: '#333' }}>
                  Scroll down the share menu list and select <strong>Add to Home Screen</strong> <AddBoxIcon fontSize="small" style={{ verticalAlign: 'middle', color: themeColor, margin: '0 2px' }} />.
                </Typography>
              </Box>

              <Box display="flex" gap="12px" alignItems="flex-start">
                <Box
                  style={{
                    backgroundColor: themeColor,
                    color: themeTxtColor,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    flexShrink: 0,
                  }}
                >
                  3
                </Box>
                <Typography variant="body2" style={{ color: '#333' }}>
                  Tap <strong>Add</strong> in the top-right corner. The MenuLive icon will be placed on your home screen instantly!
                </Typography>
              </Box>
            </Box>
          )}

          {/* Android Instructions */}
          {activeTab === 1 && (
            <Box display="flex" flexDirection="column" gap="16px">
              <Typography variant="body2" color="textSecondary">
                For Android devices running Google Chrome, you can install the app directly in one of two ways:
              </Typography>

              {deferredPrompt && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleInstallClick}
                  style={{
                    backgroundColor: themeColor,
                    color: themeTxtColor,
                    borderRadius: '12px',
                    padding: '10px',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    marginBottom: '8px',
                  }}
                >
                  Click Here to Install Directly
                </Button>
              )}

              <Box display="flex" gap="12px" alignItems="flex-start">
                <Box
                  style={{
                    backgroundColor: themeColor,
                    color: themeTxtColor,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    flexShrink: 0,
                  }}
                >
                  1
                </Box>
                <Typography variant="body2" style={{ color: '#333' }}>
                  Look for the <strong>"Add MenuLive to Home screen"</strong> banner that appears automatically, or click the direct button above.
                </Typography>
              </Box>

              <Box display="flex" gap="12px" alignItems="flex-start">
                <Box
                  style={{
                    backgroundColor: themeColor,
                    color: themeTxtColor,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    flexShrink: 0,
                  }}
                >
                  2
                </Box>
                <Typography variant="body2" style={{ color: '#333' }}>
                  If the banner does not appear, tap the Chrome menu button <MoreVertIcon fontSize="small" style={{ verticalAlign: 'middle', color: themeColor }} /> (three dots) in the top-right corner.
                </Typography>
              </Box>

              <Box display="flex" gap="12px" alignItems="flex-start">
                <Box
                  style={{
                    backgroundColor: themeColor,
                    color: themeTxtColor,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    flexShrink: 0,
                  }}
                >
                  3
                </Box>
                <Typography variant="body2" style={{ color: '#333' }}>
                  Select <strong>Install App</strong> or <strong>Add to Home screen</strong> and confirm the prompt.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Desktop Instructions */}
          {activeTab === 2 && (
            <Box display="flex" flexDirection="column" gap="16px">
              <Typography variant="body2" color="textSecondary">
                You can install MenuLive as an app on your computer (Windows, macOS, or ChromeOS):
              </Typography>

              {deferredPrompt && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleInstallClick}
                  style={{
                    backgroundColor: themeColor,
                    color: themeTxtColor,
                    borderRadius: '12px',
                    padding: '10px',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    marginBottom: '8px',
                  }}
                >
                  Click Here to Install on PC
                </Button>
              )}

              <Box display="flex" gap="12px" alignItems="flex-start">
                <Box
                  style={{
                    backgroundColor: themeColor,
                    color: themeTxtColor,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    flexShrink: 0,
                  }}
                >
                  1
                </Box>
                <Typography variant="body2" style={{ color: '#333' }}>
                  Look at the right side of the address bar at the top of your browser (Chrome/Edge). You will see an <strong>Install</strong> icon <DownloadIcon fontSize="small" style={{ verticalAlign: 'middle', color: themeColor }} /> or an icon resembling a small monitor with a down-arrow.
                </Typography>
              </Box>

              <Box display="flex" gap="12px" alignItems="flex-start">
                <Box
                  style={{
                    backgroundColor: themeColor,
                    color: themeTxtColor,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    flexShrink: 0,
                  }}
                >
                  2
                </Box>
                <Typography variant="body2" style={{ color: '#333' }}>
                  Click that icon to open the browser installation popup, then click <strong>Install</strong>.
                </Typography>
              </Box>

              <Box display="flex" gap="12px" alignItems="flex-start">
                <Box
                  style={{
                    backgroundColor: themeColor,
                    color: themeTxtColor,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    flexShrink: 0,
                  }}
                >
                  3
                </Box>
                <Typography variant="body2" style={{ color: '#333' }}>
                  The app will open immediately in a standalone, dedicated window, and a shortcut will be placed on your Desktop!
                </Typography>
              </Box>
            </Box>
          )}

          <Box
            display="flex"
            alignItems="center"
            gap="8px"
            style={{
              marginTop: '20px',
              padding: '10px 14px',
              backgroundColor: '#f1fcf1',
              borderRadius: '8px',
              border: '1px solid #d4eed4',
            }}
          >
            <CheckCircleIcon style={{ color: '#4caf50' }} />
            <Typography variant="caption" style={{ color: '#2e7d32', fontWeight: 'bold' }}>
              PWA runs faster, saves battery, and works even when you are offline!
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* CSS Animation for fadeInUp */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translate(-50%, 30px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
    </>
  );
};

export default PWAInstallPrompt;
